/* uso: onChange memorizza il nuovo json, onUpdate chiama una funzione esterna, come quella che attiva il bottone Memorizza Modifiche
  }}

<JsonOrarioEditor
  value={jsonOrario}
  onChange={setJsonOrario}
  onUpdate={(nuovoArray) => {
    // console.log("Orari aggiornati:", nuovoArray);
    // qui richiamare la funzione esterna
  }}
/>

opzionalmente, si può aggiungere il parametro:
  resetKey={versione}          // cambia numero/stringa per forzare il reload
*/

import React from "react";
import {
  Row, Col, FormGroup, Label, Input, Button, FormFeedback,
} from "reactstrap";

const DAYS = [
  { value: 1, label: "Lunedì" },
  { value: 2, label: "Martedì" },
  { value: 3, label: "Mercoledì" },
  { value: 4, label: "Giovedì" },
  { value: 5, label: "Venerdì" },
  { value: 6, label: "Sabato" },
  { value: 0, label: "Domenica" },
];

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const pad2 = (n) => String(n).padStart(2, "0");
const toMinutes = (hhmm) => /^\d{2}:\d{2}$/.test(hhmm||"") ? (+hhmm.slice(0,2))*60 + (+hhmm.slice(3,5)) : null;
const overlap = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;

const splitHHMM = (v) => (/^\d{2}:\d{2}$/.test(v||"") ? { hh:v.slice(0,2), mm:v.slice(3,5) } : { hh:"", mm:"" });

const cleanAndSort = (rows) => {
  const cleaned = rows.map(({ giorno, dalle, alle }) => ({ giorno, dalle, alle }));
  const toMin = (t) => /^\d{2}:\d{2}$/.test(t||"") ? (+t.slice(0,2))*60 + (+t.slice(3,5)) : 1e9;
  cleaned.sort((a,b) => a.giorno === b.giorno ? toMin(a.dalle)-toMin(b.dalle) : a.giorno-b.giorno);
  return cleaned;
};

/* ---- TimePicker HH/MM ---- */
function TimePicker({ value, onChange, invalid, ariaLabelHour, ariaLabelMinute }) {
  const { hh: initH, mm: initM } = splitHHMM(value);
  const [hh, setHh] = React.useState(initH);
  const [mm, setMm] = React.useState(initM);

  React.useEffect(() => { // sync esterno→interno SOLO quando cambia value esterno
    const { hh:h, mm:m } = splitHHMM(value);
    setHh(h); setMm(m);
  }, [value]);

  const commit = (hStr, mStr) => {
    const h = clamp(parseInt(hStr||"0",10), 0, 23);
    const m = clamp(parseInt(mStr||"0",10), 0, 59);
    onChange(`${pad2(h)}:${pad2(m)}`);
  };

  return (
    <div className="d-flex align-items-center" style={{ gap: 6 }}>
      <Input
        type="text" inputMode="numeric" maxLength={2}
        value={hh} onChange={(e)=>setHh(e.target.value.replace(/\D/g,"").slice(0,2))}
        onBlur={()=>commit(hh,mm)} onKeyDown={(e)=>{ if(e.key==="ArrowUp"){e.preventDefault();const v=(parseInt(hh||"0",10)+1)%24;const s=pad2(v);setHh(s);commit(s,mm);} if(e.key==="ArrowDown"){e.preventDefault();const v=(parseInt(hh||"0",10)+23)%24;const s=pad2(v);setHh(s);commit(s,mm);} }}
        invalid={invalid} autoComplete="off" aria-label={ariaLabelHour||"Ore"} className="text-center" style={{ width: 80 }} placeholder="HH"
      />
      <div>:</div>
      <Input
        type="text" inputMode="numeric" maxLength={2}
        value={mm} onChange={(e)=>setMm(e.target.value.replace(/\D/g,"").slice(0,2))}
        onBlur={()=>commit(hh,mm)} onKeyDown={(e)=>{ if(e.key==="ArrowUp"){e.preventDefault();const v=(parseInt(mm||"0",10)+1)%60;const s=pad2(v);setMm(s);commit(hh,s);} if(e.key==="ArrowDown"){e.preventDefault();const v=(parseInt(mm||"0",10)+59)%60;const s=pad2(v);setMm(s);commit(hh,s);} }}
        invalid={invalid} autoComplete="off" aria-label={ariaLabelMinute||"Minuti"} className="text-center" style={{ width: 80 }} placeholder="MM"
      />
    </div>
  );
}

/* ---- Componente principale ---- */
export default function JsonOrarioEditor({
  value = [],            // [{ giorno, dalle, alle }] — usato come valore iniziale
  onChange = () => {},   // espone SEMPRE json pulito + ORDINATO
  onUpdate = () => {},   // idem, ogni modifica
  defaultFrom = "09:00",
  defaultTo   = "18:00",
  resetKey,              // (opzionale) se cambia, ricarica "value" nel componente
}) {
  // Stato interno con id stabili — inizializzato solo al mount
  const [rows, setRows] = React.useState(() =>
    (value || []).map(r => ({ ...r, id: uid() }))
  );

  // Se vuoi forzare un reload esterno (es. dopo fetch o reset), cambia resetKey
  React.useEffect(() => {
    if (resetKey !== undefined) {
      setRows((value || []).map(r => ({ ...r, id: uid() })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emit = (nextRows) => {
    setRows(nextRows);
    const cleanedSorted = cleanAndSort(nextRows);
    onChange(cleanedSorted);
    onUpdate(cleanedSorted);
  };

  const update = (idx, patch) => emit(rows.map((r,i)=> i===idx ? { ...r, ...patch } : r));
  const addRow = () => {
    const used = new Set(rows.map(r=>r.giorno));
    const nextDay = DAYS.find(d=>!used.has(d.value))?.value ?? (rows.length ? rows[rows.length-1].giorno : 1);
    emit([...rows, { id: uid(), giorno: nextDay, dalle: defaultFrom, alle: defaultTo }]);
  };
  const removeRow = (idx) => emit(rows.filter((_,i)=>i!==idx));

  const errors = React.useMemo(() =>
    rows.map((row,i) => {
      const start = toMinutes(row.dalle);
      const end   = toMinutes(row.alle);
      const err = { time:null, overlap:false };
      if (start==null || end==null || end<=start) err.time = "L'orario 'alle' deve essere successivo a 'dalle'.";
      if (!err.time) {
        for (let j=0;j<rows.length;j++){
          if (j===i || rows[j].giorno!==row.giorno) continue;
          const s2=toMinutes(rows[j].dalle), e2=toMinutes(rows[j].alle);
          if (s2!=null && e2!=null && overlap(start,end,s2,e2)) { err.overlap=true; break; }
        }
      }
      return err;
    })
  , [rows]);

  return (
    <div className="json-orario-editor">
      {rows.map((row, idx) => {
        const err = errors[idx] ?? {};
        const hasErr = !!err.time;
        const warnOverlap = !hasErr && err.overlap;

        return (
          <div key={row.id} className="mb-2 p-2 border rounded">
            <Row className="g-2 align-items-center">
              <Col md={4}>
                <FormGroup>
                  <Label>Giorno</Label>
                  <Input
                    type="select"
                    className="form-control"
                    value={row.giorno}
                    onChange={(e)=>update(idx, { giorno: Number(e.target.value) })}
                  >
                    {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label>Dalle</Label>
                  <TimePicker
                    value={row.dalle||""}
                    onChange={(v)=>update(idx, { dalle:v })}
                    invalid={hasErr}
                    ariaLabelHour="Ore dalle"
                    ariaLabelMinute="Minuti dalle"
                  />
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label>Alle</Label>
                  <TimePicker
                    value={row.alle||""}
                    onChange={(v)=>update(idx, { alle:v })}
                    invalid={hasErr}
                    ariaLabelHour="Ore alle"
                    ariaLabelMinute="Minuti alle"
                  />
                  {hasErr && <FormFeedback>{errors[idx].time}</FormFeedback>}
                </FormGroup>
              </Col>

              <Col md={2} className="d-flex align-items-center">
                <Button color="danger" outline onClick={()=>removeRow(idx)} className="ms-auto">
                  Rimuovi
                </Button>
              </Col>
            </Row>

            {warnOverlap && (
              <div className="text-warning small mt-1">
                Attenzione: intervallo sovrapposto ad un altro dello stesso giorno.
              </div>
            )}
          </div>
        );
      })}

      <div className="d-flex">
        <Button color="primary" onClick={addRow} className="ms-auto">Aggiungi intervallo</Button>
      </div>
    </div>
  );
}
